import { Filter } from 'pixi.js';
import { shader } from './shader.js';
import { REFLECTION_DIRECTION_FOUR_WAY } from '../../../PreviewDisplayConstants.js';

/*
 * QuadrantMirrorFilter is a custom PIXI.js filter that creates a mirrored effect by reflecting 
 * specific quadrants of a texture based on the specified direction. This allows for a variety 
 * of symmetric effects by mirroring combinations of the left, right, top, and bottom parts of a texture.
 * 
 * Key Features:
 * - Mirrors specific quadrants of the texture (e.g., left-top, right-top, etc.).
 * - Configurable through the REFLECTION_DIRECTION_FOUR_WAY constant.
 * - Customizable boundary settings for fine-tuning the reflection area.
 * 
 * Shader Logic:
 * - The accompanying GLSL shader determines the quadrant of a pixel and applies mirroring 
 *   by flipping UV coordinates accordingly.
 * 
 * Usage:
 * - Instantiate the filter with a direction (e.g., REFLECTION_DIRECTION_FOUR_WAY.TOP_LEFT).
 * - Adjust the boundary property to define the quadrant threshold for the mirroring effect.
 * - Add the filter to a PIXI.Sprite or PIXI.Container.
 * 
 * Notes:
 * - Ensure the `direction` parameter passed is a valid value from REFLECTION_DIRECTION_FOUR_WAY for proper functionality.
 * - The shader dynamically adapts based on the selected quadrant direction.
 */
export class QuadrantMirrorFilter extends Filter {
    constructor() {
        super(null, shader);
        this.uniforms.waveLength = new Float32Array(2);
        this.uniforms.dimensions = new Float32Array(2);

        Object.assign(this, {
            mirror: true,
            direction: 1,
            boundary: 0.5,
            amplitude: [0, 0],
            waveLength: [10, 10]
        });
    }

    apply(filterManager, input, output, clear) {
        this.uniforms.dimensions[0] = input.filterFrame.width;
        this.uniforms.dimensions[1] = input.filterFrame.height;

        filterManager.applyFilter(this, input, output, clear);
    }

    set direction(type) {
        this.uniforms.enableLeftPart = false;
        this.uniforms.enableRightPart = false;
        this.uniforms.enableTopPart = false;
        this.uniforms.enableBottomPart = false;

        if (type === REFLECTION_DIRECTION_FOUR_WAY.RIGHT_TOP_CORNER) {
            this.uniforms.enableRightPart = true;
            this.uniforms.enableTopPart = true;
            return;
        }

        if (type === REFLECTION_DIRECTION_FOUR_WAY.LEFT_TOP_CORNER) {
            this.uniforms.enableLeftPart = true;
            this.uniforms.enableTopPart = true;
            return;
        }

        if (type === REFLECTION_DIRECTION_FOUR_WAY.RIGHT_BOTTOM_CORNER) {
            this.uniforms.enableRightPart = true;
            this.uniforms.enableBottomPart = true;
            return;
        }

        if (type === REFLECTION_DIRECTION_FOUR_WAY.LEFT_BOTTOM_CORNER) {
            this.uniforms.enableLeftPart = true;
            this.uniforms.enableBottomPart = true;
            return;
        }
    }

    get direction() {
        return this.uniforms.direction;
    }

    set waveLength(value) {
        this.uniforms.waveLength[0] = value[0];
        this.uniforms.waveLength[1] = value[1];
    }
    get waveLength() {
        return this.uniforms.waveLength;
    }
}